/**
 * Centralized language-specific image registry.
 *
 * Keys map to per-locale asset paths. Add a new language by extending each entry
 * (e.g. `de`, `ar`, `es`) — no component changes required.
 *
 * Prefer `LocalizedImage` / `useLocalizedImageSrc` over hardcoding paths.
 *
 * @typedef {Record<string, string>} LocalizedImageSources
 * @typedef {Record<string, LocalizedImageSources>} LocalizedImageRegistry
 */

/** Locales currently expected for image variants (mirrors i18n supportedLngs). */
export const IMAGE_LOCALES = Object.freeze(["tr", "en", "fr"]);

/**
 * Fallback order when a locale asset is missing:
 * active language → English → Turkish → first available path in the map.
 */
export const IMAGE_FALLBACK_LOCALES = Object.freeze(["en", "tr"]);

/**
 * @type {LocalizedImageRegistry}
 */
export const LOCALIZED_IMAGES = Object.freeze({
  "hero-device-mockup": {
    tr: "/assets/marketing/marketing-tablet-telefon-tr.webp",
    en: "/assets/marketing/marketing-tablet-telefon-en.webp",
    fr: "/assets/marketing/pm-dashboard-phone-computer-fr.webp",
  },
  "hero-data-tablet": {
    tr: "/assets/marketing/marketing-kisim2-tr.webp",
    en: "/assets/marketing/marketing-kisim2-en.webp",
    fr: "/assets/marketing/marketing-kisim2-fr.webp",
  },
  "hero-ai-llm-dashboard": {
    tr: "/assets/marketing/marketing-dashboard-llm-tr.webp",
    en: "/assets/marketing/marketing-kisim3-en.webp",
    fr: "/assets/marketing/marketing-kisim3-fr.webp",
  },
  "app-mockup": {
    tr: "/assets/app-mockup-shots-tr.webp",
    en: "/assets/app-mockup-shots-en.webp",
    fr: "/assets/app-mockup-shots-fr.webp",
  },
  "excel-import": {
    tr: "/assets/marketing/marketing-excel-tr.webp",
    en: "/assets/marketing/marketing-excel-en.webp",
    fr: "/assets/marketing/marketing-excel-fr.webp",
  },
  "analytics-scientific": {
    tr: "/assets/marketing/marketing-bilimsel-tr.webp",
    en: "/assets/marketing/marketing-bilimsel-en.webp",
    fr: "/assets/marketing/marketing-bilimsel-fr.webp",
  },
  "analytics-ml": {
    tr: "/assets/marketing/marketing-ml-tr.webp",
  },
  "analytics-ai-clinical": {
    tr: "/assets/marketing/marketing-ai-meshai-tr.webp",
  },
  "interactive-protocol": {
    tr: "/assets/marketing/marketing-takip.webp",
  },
  "interactive-formula": {
    tr: "/assets/marketing/marketing-skorlama.webp",
  },
  "interactive-visualization": {
    tr: "/assets/marketing/marketing-klinik-gorsellestirme.webp",
  },
  "interactive-integration": {
    tr: "/assets/marketing/Marketing-HBYS-Entegrasyon.png",
  },
  "data-journey-permissions": {
    tr: "/assets/marketing/marketing-yetki-islemleri.webp",
  },
  "showcase-llm": {
    tr: "/assets/llm-infographic.webp",
    en: "/assets/llm-infographic.webp",
    fr: "/assets/llm-infographic.webp",
  },
  "showcase-cloud": {
    tr: "/assets/cloud-infrastructure.webp",
    en: "/assets/cloud-infrastructure.webp",
    fr: "/assets/cloud-infrastructure.webp",
  },
  "showcase-ai-platform": {
    tr: "/assets/ai-health-platform.webp",
    en: "/assets/ai-health-platform.webp",
    fr: "/assets/ai-health-platform.webp",
  },
  "cloud-security": {
    tr: "/assets/cloud-security.webp",
    en: "/assets/cloud-security.webp",
    fr: "/assets/cloud-security.webp",
  },
});

/**
 * @param {string} imageKey
 * @returns {LocalizedImageSources | undefined}
 */
export function getLocalizedImageEntry(imageKey) {
  return LOCALIZED_IMAGES[imageKey];
}
